declare global {
  interface Window {
    dataLayer: unknown[];
    gtag?: (...args: unknown[]) => void;
    __aboeloGtagConsentDefault?: boolean;
  }
}

/** Persisted user choice for the cookie banner (Google Consent Mode v2). */
export const COOKIE_CONSENT_STORAGE_KEY = 'aboelo_cookie_consent';
export type CookieConsentChoice = 'all' | 'necessary';

const CONSENT_DENIED_MEASUREMENT = {
  ad_storage: 'denied',
  analytics_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
} as const;

const CONSENT_GRANTED_MEASUREMENT = {
  ad_storage: 'granted',
  analytics_storage: 'granted',
  ad_user_data: 'granted',
  ad_personalization: 'granted',
} as const;

let measurementIdCache: string | null = null;

export const getStoredConsentChoice = (): CookieConsentChoice | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    // 1. Check local storage (both keys and formats)
    const raw = localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
    if (raw === 'all' || raw === 'accepted') return 'all';
    if (raw === 'necessary' || raw === 'rejected') return 'necessary';

    // 2. Check cookies
    const cookies = document.cookie.split(';');
    for (let c of cookies) {
      c = c.trim();
      if (c.startsWith('rcb-consent=')) {
        const cookieValue = decodeURIComponent(c.substring('rcb-consent='.length));
        if (cookieValue.includes('"statistics":true') || cookieValue.includes('"analytics":true') || cookieValue.includes('"statistics-optin":true')) {
          localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, 'all');
          return 'all';
        } else if (cookieValue.includes('"statistics":false') || cookieValue.includes('"analytics":false') || cookieValue.includes('"statistics-optin":false')) {
          localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, 'necessary');
          return 'necessary';
        }
      }
      if (c.startsWith('aboelo_cookie_consent=')) {
        const sharedValue = c.substring('aboelo_cookie_consent='.length).trim();
        if (sharedValue === 'accepted' || sharedValue === 'all') {
          localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, 'all');
          return 'all';
        } else if (sharedValue === 'rejected' || sharedValue === 'necessary') {
          localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, 'necessary');
          return 'necessary';
        }
      }
    }
  } catch {
    /* ignore */
  }
  return null;
};

const ensureGtagFunction = () => {
  if (!window.dataLayer) {
    window.dataLayer = [];
  }

  if (!window.gtag) {
    window.gtag = (...args: unknown[]) => {
      window.dataLayer.push(args);
    };
  }
};

/**
 * Consent defaults must run before gtag.js loads. index.html sets them and sets
 * __aboeloGtagConsentDefault; this fallback covers tests or non-standard entry.
 */
const ensureConsentDefault = () => {
  if (typeof window === 'undefined') {
    return;
  }
  ensureGtagFunction();
  if (window.__aboeloGtagConsentDefault) {
    return;
  }
  window.gtag?.('consent', 'default', {
    ...CONSENT_DENIED_MEASUREMENT,
    functionality_storage: 'granted',
    personalization_storage: 'denied',
    security_storage: 'granted',
    wait_for_update: 500,
  });
  window.__aboeloGtagConsentDefault = true;
};

const safeGtag = (...args: unknown[]) => {
  if (typeof window !== 'undefined') {
    ensureGtagFunction();
    window.gtag?.(...args);
  }
};

/** Apply Consent Mode update (call after user choice or when restoring from storage). */
export const pushConsentUpdate = (choice: CookieConsentChoice) => {
  if (typeof window === 'undefined') {
    return;
  }
  ensureGtagFunction();
  if (choice === 'all') {
    window.gtag?.('consent', 'update', { ...CONSENT_GRANTED_MEASUREMENT });
  } else {
    window.gtag?.('consent', 'update', { ...CONSENT_DENIED_MEASUREMENT });
  }
};

export const setConsentChoice = (choice: CookieConsentChoice) => {
  if (typeof localStorage === 'undefined') {
    return;
  }
  try {
    localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, choice);
    document.cookie = `aboelo_cookie_consent=${choice}; path=/; max-age=31536000; SameSite=Lax`;
  } catch {
    /* ignore */
  }
  pushConsentUpdate(choice);
};

export const initializeAnalytics = (measurementId?: string) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  if (!measurementId) {
    console.warn('[Analytics] Missing GA measurement ID. Skipping initialization.');
    return;
  }

  ensureConsentDefault();

  const stored = getStoredConsentChoice();
  if (stored === 'all') {
    pushConsentUpdate('all');
  }

  if (measurementIdCache === measurementId && document.querySelector(`script[src*="googletagmanager.com/gtag/js"]`)) {
    return;
  }

  measurementIdCache = measurementId;

  const scriptSrc = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  const existingScript = document.querySelector(`script[src="${scriptSrc}"]`);

  if (!existingScript) {
    const script = document.createElement('script');
    script.async = true;
    script.src = scriptSrc;
    document.head.appendChild(script);
  }

  safeGtag('js', new Date());
  safeGtag('config', measurementId, { anonymize_ip: true });
};

export const trackPageView = (path: string) => {
  if (typeof window === 'undefined' || !measurementIdCache) {
    return;
  }

  safeGtag('config', measurementIdCache, {
    page_path: path,
  });
};

export const trackEvent = (action: string, params: Record<string, unknown> = {}) => {
  if (typeof window === 'undefined' || !measurementIdCache) {
    return;
  }

  safeGtag('event', action, params);
};
